import { useAsesorEmpresas, useDashboardAsesores } from '@/hooks';
import { useImpersonation } from '@/hooks/useImpersonation';
import { ColumnPicker, useColumnVisibility, type ColDef } from '@/components/ui/ColumnPicker';
import type { AsesorMetrics } from '@/types/types';
import { dashboardService } from '@/services/dashboard.service';
import { ChevronDown, ChevronUp, Download, LogIn, Mail, Search } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ── AsesorDetail empresas column defs ────────────────

type AsesorEmpresaColKey = 'empresa' | 'nit' | 'norma' | 'horas' | 'progreso' | 'estado';

const ASESOR_EMPRESA_COLS: ColDef<AsesorEmpresaColKey>[] = [
  { key: 'empresa', label: 'Empresa', defaultVisible: true },
  { key: 'nit', label: 'NIT', defaultVisible: true },
  { key: 'norma', label: 'Norma', defaultVisible: true },
  { key: 'horas', label: 'Horas', defaultVisible: true },
  { key: 'progreso', label: 'Progreso', defaultVisible: true },
  { key: 'estado', label: 'Estado', defaultVisible: true },
];

type AsesorColKey = 'asesor' | 'correo' | 'empresas' | 'activas' | 'horas';

const ASESORES_COLS: ColDef<AsesorColKey>[] = [
  { key: 'asesor', label: 'Asesor', defaultVisible: true },
  { key: 'correo', label: 'Correo', defaultVisible: true },
  { key: 'empresas', label: 'Empresas', defaultVisible: true },
  { key: 'activas', label: 'Activas', defaultVisible: true },
  { key: 'horas', label: '% Horas', defaultVisible: true },
];

// ─── Helpers ─────────────────────────────────────────

function formatHours(h: number): string {
  const t = Math.round(h * 3600);
  const hrs = Math.floor(t / 3600);
  const min = Math.floor((t % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function getInitials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

function pColor(v: number): string {
  if (v >= 70) return 'var(--admin-green)';
  if (v >= 50) return '#e09b00';
  return 'var(--admin-red)';
}

const AVATAR_COLORS = [
  { bg: 'var(--admin-blue-bg)', text: 'var(--admin-blue)' },
  { bg: 'var(--admin-green-bg)', text: 'var(--admin-green)' },
  { bg: 'var(--admin-purple-bg)', text: 'var(--admin-purple)' },
  { bg: 'var(--admin-amber-bg)', text: 'var(--admin-amber)' },
];

function ProgBar({ value, color }: { value: number; color: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="admin-prog-bar">
        <span className="admin-prog-fill" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
      </span>
      <span className="text-[12px] font-medium">{value.toFixed(0)}%</span>
    </span>
  );
}

// ─── Asesor Detail Panel ──────────────────────────────

function AsesorDetail({ asesor, idx }: { asesor: AsesorMetrics; idx: number }) {
  const navigate = useNavigate();
  const { canImpersonate, impersonate } = useImpersonation();
  const { empresas, isLoading } = useAsesorEmpresas(asesor.asesorId);
  const [impersonating, setImpersonating] = useState(false);
  const colors = AVATAR_COLORS[idx % AVATAR_COLORS.length];

  const {
    visible: colVisible,
    toggle: toggleCol,
    reset: resetCols,
    activeCount: colCount,
  } = useColumnVisibility<AsesorEmpresaColKey>(ASESOR_EMPRESA_COLS, 'tourixia:admin-asesor-empresas-cols');

  const handleImpersonate = async () => {
    setImpersonating(true);
    try {
      await impersonate(Number(asesor.asesorId));
      window.location.replace('/');
    } catch {
      /* ignore */
    } finally {
      setImpersonating(false);
    }
  };

  return (
    <>
      {/* Detail Panel */}
      <div className="admin-detail-panel">
        <div className="flex items-start gap-4 mb-5">
          <div className="admin-avatar" style={{ background: colors.bg, color: colors.text }}>
            {getInitials(asesor.nombre)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-semibold text-(--admin-text) mb-0.5">{asesor.nombre}</div>
            <div className="text-[12px] text-(--admin-text3) flex items-center gap-1">
              <span>ASESOR</span> · <Mail className="w-3 h-3 inline" /> {asesor.correo}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <span className="admin-chip bg-(--admin-green-bg) text-(--admin-green)">Activo</span>
            <span className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue)">{asesor.totalEmpresas} empresas</span>
          </div>
        </div>

        <div className="admin-detail-grid">
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Asesorías finalizadas</div>
            <div className="text-[13px] font-medium">{asesor.asesoriasFinalizadas}</div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Asesoría activa</div>
            <div>
              {asesor.asesoriasActivas > 0 ? (
                <span className="admin-chip bg-(--admin-green-bg) text-(--admin-green)">En curso</span>
              ) : (
                <span className="admin-chip bg-(--admin-surface2) text-(--admin-text2)">Sin activa</span>
              )}
            </div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Avance de Horas</div>
            <div
              className="text-[13px] font-medium cursor-help hover:text-(--admin-blue) transition-colors"
              title={`${formatHours(asesor.horasTotales)} / ${formatHours(asesor.totalEmpresas * 320)}`}
            >
              {asesor.totalEmpresas > 0 ? ((asesor.horasTotales / (asesor.totalEmpresas * 320)) * 100).toFixed(1) : '0.0'}%
            </div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Cumpl. promedio</div>
            <div className="text-[13px] font-medium">{asesor.cumplimientoPromedio.toFixed(1)}%</div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Empresas activas</div>
            <div className="text-[13px] font-medium">{asesor.totalEmpresas}</div>
          </div>
          <div className="admin-detail-field">
            <div className="text-[10px] text-(--admin-text3) uppercase tracking-wider mb-1">Rol</div>
            <div className="text-[13px] font-medium">ASESOR</div>
          </div>
        </div>

        {canImpersonate && (
          <button
            type="button"
            onClick={handleImpersonate}
            disabled={impersonating}
            className="admin-btn flex items-center gap-2 text-[12px] bg-(--admin-amber-bg) text-(--admin-amber) border-transparent hover:opacity-80 disabled:opacity-50"
          >
            <LogIn className="w-3.5 h-3.5" />
            {impersonating ? 'Accediendo...' : 'Acceder como este asesor'}
          </button>
        )}
      </div>

      {/* Empresas Table */}
      <div className="admin-card">
        <div className="admin-card-header flex items-center justify-between gap-3">
          <div className="text-[13px] font-semibold text-(--admin-text)">Empresas asignadas</div>
          <ColumnPicker cols={ASESOR_EMPRESA_COLS} visible={colVisible} onToggle={toggleCol} onReset={resetCols} activeCount={colCount} />
        </div>
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="w-5 h-5 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  {ASESOR_EMPRESA_COLS.filter((c) => colVisible[c.key]).map((c) => (
                    <th key={c.key} className="text-left">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {empresas.map((emp) => {
                  const c = emp.porcentajeCumplimiento ?? 0;
                  const prog = emp.progresoDetallado;
                  const status = emp.estadoRelacion || (emp.asesoriaActivaId ? 'EN_CURSO' : 'PENDIENTE');

                  return (
                    <tr key={emp.nitId} className="clickable" onClick={() => navigate(`/empresas/${emp.nitId}`)}>
                      {colVisible.empresa && <td className="font-medium text-(--admin-text) max-w-48 truncate">{emp.nombre}</td>}
                      {colVisible.nit && <td className="font-mono text-[11px] text-(--admin-text3) whitespace-nowrap">{emp.nitId}</td>}
                      {colVisible.norma && (
                        <td className="whitespace-nowrap">
                          <span
                            className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue) text-[10px] max-w-72 truncate block"
                            title={emp.combinacionNtc?.nombre ?? 'Sin NTC'}
                          >
                            {emp.combinacionNtc?.nombre ?? 'Sin NTC'}
                          </span>
                        </td>
                      )}
                      {colVisible.horas && <td className="text-center font-medium whitespace-nowrap">{formatHours(emp.horasAsesoria)}</td>}
                      {colVisible.progreso && (
                        <td className="min-w-40 whitespace-nowrap">
                          <div className="flex flex-col gap-1.5 py-1">
                            <ProgBar value={c} color={pColor(c)} />
                            {prog && (
                              <div className="flex items-center gap-2 text-[10px] text-(--admin-text3) font-medium">
                                <span className="text-(--admin-green)">C:{prog.C}</span>
                                <span className="text-(--admin-red)">NC:{prog.NC}</span>
                                <span className="text-(--admin-amber)">CP:{prog.CP}</span>
                                <span className="opacity-50">NA:{prog.NA}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                      {colVisible.estado && (
                        <td className="text-right whitespace-nowrap">
                          {status === 'EN_CURSO' && (
                            <span className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue) animate-pulse border-(--admin-blue-bg)">
                              En curso
                            </span>
                          )}
                          {status === 'EN_REVISION' && (
                            <span className="admin-chip bg-(--admin-amber-bg) text-(--admin-amber)">En revisión</span>
                          )}
                          {status === 'FINALIZADA' && (
                            <span className="admin-chip bg-(--admin-green-bg) text-(--admin-green)">Finalizada</span>
                          )}
                          {status === 'PENDIENTE' && (
                            <span className="admin-chip bg-(--admin-surface2) text-(--admin-text2)">Pendiente</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {empresas.length === 0 && (
                  <tr>
                    <td
                      colSpan={ASESOR_EMPRESA_COLS.filter((c) => colVisible[c.key]).length}
                      className="text-center py-6 text-(--admin-text3)"
                    >
                      Sin empresas asignadas
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Main Page ─────────────────────────────────────

export function AdminAsesoresPage() {
  const navigate = useNavigate();
  const { asesores, isLoading, error } = useDashboardAsesores();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadExcelHoras = async () => {
    setDownloading(true);
    try {
      const blob = await dashboardService.downloadHorasAsesoresExcelReport();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reporte_Horas_Asesores_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar reporte de horas:', err);
    } finally {
      setDownloading(false);
    }
  };

  const {
    visible: mainVisible,
    toggle: toggleMain,
    reset: resetMain,
    activeCount: mainCount,
  } = useColumnVisibility<AsesorColKey>(ASESORES_COLS, 'tourixia:admin-asesores-cols');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return asesores;
    return asesores.filter((a) => a.nombre.toLowerCase().includes(q) || a.correo.toLowerCase().includes(q));
  }, [asesores, search]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => a.nombre.localeCompare(b.nombre)), [filtered]);

  const selected = asesores.find((a) => a.asesorId === selectedId) ?? null;
  const selectedIdx = asesores.findIndex((a) => a.asesorId === selectedId);

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <div className="w-6 h-6 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center text-(--admin-red)">
        <p className="font-medium mb-1">Error al cargar asesores</p>
        <p className="text-[13px] text-(--admin-text3)">{error}</p>
      </div>
    );
  }

  return (
    <>
      {/* Search */}
      <div className="admin-search-row flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-(--admin-text3)" />
            <input
              type="text"
              placeholder="Buscar asesor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admin-search-input w-55"
            />
          </div>
          <button
            type="button"
            onClick={handleDownloadExcelHoras}
            disabled={downloading}
            className="admin-btn text-[12px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 flex items-center gap-1.5 disabled:opacity-50"
            title="Descargar consolidado en Excel (3 hojas: resumen, detalle y reasignaciones)"
          >
            <Download className="w-3.5 h-3.5 text-emerald-600" />
            {downloading ? 'Descargando...' : 'Reporte Excel Horas'}
          </button>
        </div>
        <ColumnPicker cols={ASESORES_COLS} visible={mainVisible} onToggle={toggleMain} onReset={resetMain} activeCount={mainCount} />
      </div>

      {/* Table */}
      <div className="admin-card">
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                {mainVisible.asesor && <th className="whitespace-nowrap">Asesor</th>}
                {mainVisible.correo && <th className="whitespace-nowrap">Correo</th>}
                {mainVisible.empresas && <th className="whitespace-nowrap">Empresas</th>}
                {mainVisible.activas && <th className="whitespace-nowrap">Activas</th>}
                {mainVisible.horas && <th className="whitespace-nowrap">Horas</th>}
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a, idx) => {
                const isSelected = a.asesorId === selectedId;
                return (
                  <Fragment key={a.asesorId}>
                    <tr
                      className={`clickable ${isSelected ? 'bg-(--admin-surface2)!' : ''}`}
                      onClick={() => navigate(`/asesores/${a.asesorId}`)}
                    >
                      {mainVisible.asesor && (
                        <td className="font-medium text-(--admin-text) whitespace-nowrap max-w-48 truncate">{a.nombre}</td>
                      )}
                      {mainVisible.correo && <td className="text-(--admin-text2) whitespace-nowrap">{a.correo}</td>}
                      {mainVisible.empresas && <td className="whitespace-nowrap">{a.totalEmpresas}</td>}
                      {mainVisible.activas && <td className="font-medium whitespace-nowrap">{a.asesoriasActivas}</td>}
                      {mainVisible.horas && (
                        <td
                          className="text-(--admin-text2) whitespace-nowrap cursor-help hover:text-(--admin-blue) transition-colors"
                          title={`${formatHours(a.horasTotales)} / ${formatHours(a.totalEmpresas * 320)}`}
                        >
                          {a.totalEmpresas > 0 ? ((a.horasTotales / (a.totalEmpresas * 320)) * 100).toFixed(1) : '0.0'}%
                        </td>
                      )}
                      <td className="whitespace-nowrap">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-(--admin-surface3) transition-colors cursor-pointer"
                            title={isSelected ? 'Ocultar detalle' : 'Mostrar detalle rápido'}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              setSelectedId(isSelected ? null : a.asesorId);
                            }}
                          >
                            {isSelected ? (
                              <ChevronUp className="w-4 h-4 text-(--admin-text2)" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-(--admin-text2)" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isSelected && (
                      <tr className="bg-(--admin-surface2) shadow-inner border-y border-(--admin-border)">
                        <td colSpan={6} className="p-0">
                          <div className="p-4 sm:p-6 bg-(--admin-bg) m-2 rounded-xl border border-(--admin-border) shadow-sm">
                            <AsesorDetail asesor={a} idx={idx} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-(--admin-text3)">
                    Sin asesores encontrados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
